/* eslint-disable no-underscore-dangle */
/* eslint-disable react/destructuring-assignment */
import cx from 'classnames'
import PropTypes from 'prop-types'
import React, { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { Box } from '@material-ui/core'
import { recording as recordingSelectors } from 'selectors'
// import { ReactComponent as DoubleRewindIcon } from '~/assets/video-player/ic_double_rewind.svg'
import { HOTKEYS, PLAYER_TYPE } from 'enums'
import { useHotkeys } from 'react-hotkeys-hook'
import { activeHotkeyProfileSelector } from 'selectors/user-profile-selector'
import { TranslationContext } from 'contexts/TranslationContext'
import { useHotkeyTooltip } from 'hooks/useHotkeyTooltip'
import { TIME_CODE_CONTAINER_LOCATION } from 'config/constants/timecode'
import { MARK_TYPE } from 'config/constants/preview'
import { DEFAULT_FPS, timelineTimeToSeconds } from '~/Util'
import { ReactComponent as PlayIcon } from '~/assets/video-player/ic_play.svg'
import { ReactComponent as FrameStepIcon } from '~/assets/video-player/ic_rewind.svg'
import { ReactComponent as StopIcon } from '~/assets/video-player/ic_stop.svg'
import { ReactComponent as MarkInIcon } from '~/assets/preview/mark_in.svg'
import { ReactComponent as MarkOutIcon } from '~/assets/preview/mark_out.svg'
import RawButton from '~/components/base/RawButton'
import Slider from '~/components/base/Slider'
import * as PT from '~/PropTypes'

import './PreviewToolbar.scss'
import { getProjectParams, useFrameStepHandlers, useTimelineRewindHandler } from './TimelinePreview/lib'
import { TimeCode } from '~/components/Common/TimeCode'
import { ClipCreatorBar } from './ClipCreator/ClipCreatorBar'
import { PreviewSliderContextMenu } from './PreviewSliderContextMenu'
import { useClipCreator } from '~/hooks/preview/clipCreator/useClipCreator'
import { selectorActivePreview, selectorSplitPlayers } from '~/selectors/preview'
import { usePreviewSliderProgress } from '~/hooks/preview/usePreviewSliderProgress'
import { addSourceFileAtSliderPosition } from '~/actions/addSourceFileAtSliderPosition'
import { selectSelectedSourceMediaFileIds, selectViewSettings } from '~/selectors/mainView'
import { ViewSettings } from '~/config/constants/editorSettings'
import { useAddSourceFileToTimeline } from '~/hooks/useAddSourceFileToTimeline'
import { useSliderTime } from '~/providers/SliderTimeContextProvider'
import { useTimelineMarkerCommands } from '~/hooks/timeline/iopoints/useTimelineMarkerCommands'

function PreviewToolbar(props) {
  const {
    playing,
    fps,
    onPlayPause,
    onRewindTimeline,
    onPrevFrame,
    onNextFrame,
    duration,
    progress,
    isMedia,
    previewPlayerType,
    previewPlayerVideoElem,
    asset,
    isLoading,
  } = props

  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const project = useSelector(getProjectParams)
  const selectedMediaFileIds = useSelector(selectSelectedSourceMediaFileIds)
  const mediaRecording = useSelector(recordingSelectors.isRecordingStarted)
  const mediaCapturing = useSelector(recordingSelectors.isRecordingCapture)
  const isSaving = useSelector(recordingSelectors.isRecordingSaving)
  const recordingCountdownStarted = useSelector(recordingSelectors.selectShowCountdown)
  const isSplittedPreviewPlayers = useSelector(selectorSplitPlayers)
  const localTime = useSliderTime()
  const { t } = useContext(TranslationContext)
  const { getTooltip } = useHotkeyTooltip()
  const onAddToTimeline = useAddSourceFileToTimeline(
    addSourceFileAtSliderPosition, selectedMediaFileIds
  )

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.ADD_TO_TIMELINE], onAddToTimeline)


  const disabledCtrls = (mediaRecording || isSaving) && !mediaCapturing
  // TODO: Refactor this selector for separate of sliderTime and move this into the TimelinePreview component
  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const _duration = timelineTimeToSeconds(project.duration)
  const _progress = timelineTimeToSeconds(localTime)

  const _onRewindTimeline = useTimelineRewindHandler()
  const {
    onPrevFrame: _onPrevFrame,
    onNextFrame: _onNextFrame,
  } = useFrameStepHandlers(fps, previewPlayerType)
  // ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^

  const prevFrame = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    const cb = onPrevFrame || _onPrevFrame
    cb(e)
  }, [ onPrevFrame, _onPrevFrame ])

  const nextFrame = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    const cb = onNextFrame || _onNextFrame
    cb(e)
  }, [ onNextFrame, _onNextFrame ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.PREV_FRAME], prevFrame)
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.NEXT_FRAME], nextFrame)

  const previewType = isMedia ? PLAYER_TYPE.MEDIA : PLAYER_TYPE.TIMELINE
  const viewSettings = useSelector(selectViewSettings)
  const showInOutPointsButtons = previewType !== PLAYER_TYPE.TIMELINE
    ? viewSettings[ViewSettings.showInOutPointsSource]
    : viewSettings[ViewSettings.showInOutPointsTimeline]

  const {
    onRewind,
    onTogglePlayPause,
    clipDuration,
    previewProgress,
    currentFps,
    handlePrevFrame,
    handleNextFrame,
    startTime,
  } = usePreviewSliderProgress({
    isMedia,
    mediaAsset: asset,
    onPlayPause,
    fps,
    onRewindTimeline: isMedia ? onRewindTimeline : _onRewindTimeline,
    previewDration: isMedia ? duration : _duration,
    progress: isMedia ? progress : _progress,
    onPrevFrame: isMedia ? onPrevFrame : _onPrevFrame,
    onNextFrame: isMedia ? onNextFrame : _onNextFrame,
    preview: previewType,
  })

  const {
    onSetMarker,
    getTimePointToPercent,
    onClearInOutPoints,
    sliderContainerRef,
    onOpenMenu,
    onCloseMenu,
    anchorEl,
    anchorPosition,
    onClickMarkIcon,
    showInOutPoints,
    clipMarkers,
    onSetDragging,
    isDragging,
    isResize,
    onClickMarker,
    ioPointsBarRef,
    sliderProgress,
    rightTimeCodeDuration,
  } = useClipCreator({
    preview: previewType,
    progress: previewProgress,
    previewPlayerVideoElem,
    duration: clipDuration,
    asset,
    currentFps,
    onRewind,
    isMedia,
    onTogglePlayPause,
    disabledCtrls,
    canSetInOutPoints: Boolean(showInOutPointsButtons),
  })

  const activePreview = useSelector(selectorActivePreview)
  const {
    onSetMarker: onSetTimelineMarker,
    onClearInOutPoints: onClearTimelineMarkers,
  } = useTimelineMarkerCommands()
  const isTimelinePreview = previewType === PLAYER_TYPE.TIMELINE

  const onMenuMarkIn = useCallback((e, data) => {
    if (isTimelinePreview) {
      onSetTimelineMarker(MARK_TYPE.IN)
      onCloseMenu()
    } else {
      onSetMarker(previewType, MARK_TYPE.IN)(e, data)
    }
  }, [ isTimelinePreview, onSetTimelineMarker, onCloseMenu, onSetMarker, previewType ])

  const onMenuMarkOut = useCallback((e, data) => {
    if (isTimelinePreview) {
      onSetTimelineMarker(MARK_TYPE.OUT)
      onCloseMenu()
    } else {
      onSetMarker(previewType, MARK_TYPE.OUT)(e, data)
    }
  }, [ isTimelinePreview, onSetTimelineMarker, onCloseMenu, onSetMarker, previewType ])

  const onMenuClearInOutPoints = useCallback((e, data) => {
    if (isTimelinePreview) {
      onClearTimelineMarkers()
      onCloseMenu()
    } else {
      onClearInOutPoints(previewType)(e, data)
    }
  }, [ isTimelinePreview, onClearTimelineMarkers, onCloseMenu, onClearInOutPoints, previewType ])

  const onMarkInClick = useCallback((e, data) => {
    if (isTimelinePreview) {
      e.stopPropagation()
      onSetTimelineMarker(MARK_TYPE.IN)
    } else {
      onClickMarker(previewType, MARK_TYPE.IN)(e, data)
    }
  }, [ isTimelinePreview, onSetTimelineMarker, onClickMarker, previewType ])

  const onMarkOutClick = useCallback((e, data) => {
    if (isTimelinePreview) {
      e.stopPropagation()
      onSetTimelineMarker(MARK_TYPE.OUT)
    } else {
      onClickMarker(previewType, MARK_TYPE.OUT)(e, data)
    }
  }, [ isTimelinePreview, onSetTimelineMarker, onClickMarker, previewType ])

  const onTimelineMarkInHotkey = useCallback(e => {
    if (activePreview !== PLAYER_TYPE.TIMELINE) return
    if (e) e.stopPropagation()
    onSetTimelineMarker(MARK_TYPE.IN)
  }, [ activePreview, onSetTimelineMarker ])

  const onTimelineMarkOutHotkey = useCallback(e => {
    if (activePreview !== PLAYER_TYPE.TIMELINE) return
    if (e) e.stopPropagation()
    onSetTimelineMarker(MARK_TYPE.OUT)
  }, [ activePreview, onSetTimelineMarker ])

  const onTimelineClearHotkey = useCallback(e => {
    if (activePreview !== PLAYER_TYPE.TIMELINE) return
    if (e) e.stopPropagation()
    onClearTimelineMarkers()
  }, [ activePreview, onClearTimelineMarkers ])

  const timelineMarkerHotkeysEnabled = isTimelinePreview
    && !disabledCtrls
    && Boolean(showInOutPointsButtons)

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.MARK_IN],
    onTimelineMarkInHotkey, { enabled: timelineMarkerHotkeysEnabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.MARK_OUT],
    onTimelineMarkOutHotkey, { enabled: timelineMarkerHotkeysEnabled })
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.CLEAR_IN_OUT_MARK_POINTS],
    onTimelineClearHotkey, { enabled: timelineMarkerHotkeysEnabled })

  return (
    <div className={cx('preview-player-toolbar', { fullScreen: document.fullscreenElement })}>
      <div onContextMenu={onOpenMenu} className={cx('preview-player-toolbar__timeline', { 'preview-player-toolbar--disabled': disabledCtrls })}>
        <ClipCreatorBar
          duration={clipDuration}
          clipMarkers={clipMarkers}
          getTimePointToPercent={getTimePointToPercent}
          onClickMarkIcon={onClickMarkIcon}
          showInOutPoints={showInOutPoints}
          onSetDragging={onSetDragging}
          isDragging={isDragging}
          isResize={isResize}
          isLoading={isLoading}
          isSplittedPreviewPlayers={isSplittedPreviewPlayers}
          ref={ioPointsBarRef}
        />
        <PreviewSliderContextMenu
          anchorEl={anchorEl}
          anchorPosition={anchorPosition}
          onCloseMenu={onCloseMenu}
          onMarkIn={onMenuMarkIn}
          onMarkOut={onMenuMarkOut}
          onClearInOutPoints={onMenuClearInOutPoints}
          clipMarkers={clipMarkers}
          isMedia={isMedia}
          showInOutPointsButtons={Boolean(showInOutPointsButtons)}
        />
        <Slider
          className="preview-player-toolbar__slider"
          value={sliderProgress}
          onChange={onRewind}
          max={clipDuration}
          step={0.01}
          ref={sliderContainerRef}
          onOpenMenu={onOpenMenu}
          disabled={disabledCtrls}
          aria-label="preview-player-toolbar__slider"
          isPreview
        />
      </div>
      <div className="preview-player-toolbar__buttons">
        <div className="preview-player-toolbar__left">
          <TimeCode
            progress={sliderProgress}
            containerLocation={isMedia
              ? TIME_CODE_CONTAINER_LOCATION.SOURCE_MEDIA
              : TIME_CODE_CONTAINER_LOCATION.TIMELINE}
            onRewind={onRewind}
            fps={fps}
            previewPlayerType={previewPlayerType}
            startTime={startTime}
            isMedia={isMedia}
            readonly={disabledCtrls}
          />
        </div>

        <div className="preview-player-toolbar__center">
          {/* <If condition={__CFG__.PREVIEW.KEY_FRAME_STEPPER}>
            <div className="preview-player-toolbar__item">
              <RawButton disabled={disabledCtrls}>
                <DoubleRewindIcon />
              </RawButton>
            </div>
          </If> */}
          <If condition={!document.fullscreenElement && Boolean(showInOutPointsButtons)}>
            <div
              className={cx('preview-player-toolbar__item')}
              title={getTooltip(t('PREVIEW_BTN_MARK_IN_TOOLTIP'), HOTKEYS.MARK_IN)}
            >
              <RawButton disabled={disabledCtrls} ariaLabel={t('PREVIEW_BTN_MARK_IN')} onClick={onMarkInClick} dataLangId="PREVIEW_BTN_MARK_IN">
                <MarkInIcon />
              </RawButton>
            </div>

            <div
              className={cx('preview-player-toolbar__item')}
              title={getTooltip(t('PREVIEW_BTN_MARK_OUT_TOOLTIP'), HOTKEYS.MARK_OUT)}
            >
              <RawButton disabled={disabledCtrls} ariaLabel={t('PREVIEW_BTN_MARK_OUT')} onClick={onMarkOutClick} dataLangId="PREVIEW_BTN_MARK_OUT">
                <MarkOutIcon />
              </RawButton>
            </div>
          </If>

          <div className="preview-player-toolbar__item" title={getTooltip(t('PREVIEW_BTN_PREV_TOOLTIP'), HOTKEYS.PREV_FRAME)}>
            <RawButton onClick={handlePrevFrame} ariaLabel={t('PREVIEW_BTN_PREV_TOOLTIP')} disabled={disabledCtrls} dataLangId="PREVIEW_BTN_PREV">
              <FrameStepIcon />
            </RawButton>
          </div>

          <div className="preview-player-toolbar__item" title={getTooltip(!playing ? t('PREVIEW_BTN_PLAY_TOOLTIP') : t('PREVIEW_BTN_STOP_TOOLTIP'), HOTKEYS.PLAY_PAUSE)}>
            <RawButton onClick={onTogglePlayPause} ariaLabel={t('PREVIEW_BTN_PLAY_TOOLTIP')} disabled={recordingCountdownStarted || disabledCtrls} dataLangId={!playing ? 'PREVIEW_BTN_PLAY' : 'PREVIEW_BTN_STOP'}>
              {playing ? <StopIcon /> : <PlayIcon />}
            </RawButton>
          </div>

          <div className="preview-player-toolbar__item" title={getTooltip(t('PREVIEW_BTN_NEXT_TOOLTIP'), HOTKEYS.NEXT_FRAME)}>
            <RawButton onClick={handleNextFrame} ariaLabel={t('PREVIEW_BTN_NEXT_TOOLTIP')} disabled={disabledCtrls} dataLangId="PREVIEW_BTN_NEXT">
              <FrameStepIcon className="revert-icon" />
            </RawButton>
          </div>

          {/* <If condition={__CFG__.PREVIEW.KEY_FRAME_STEPPER}>
            <div className="preview-player-toolbar__item">
              <RawButton disabled={disabledCtrls}>
                <DoubleRewindIcon className="revert-icon" />
              </RawButton>
            </div>
          </If> */}
        </div>
        <Box className="preview-player-toolbar__right" gridGap={20}>
          <If condition={!document.fullscreenElement}>
            <TimeCode
              progress={rightTimeCodeDuration}
              containerLocation={isMedia
                ? TIME_CODE_CONTAINER_LOCATION.SOURCE_MEDIA
                : TIME_CODE_CONTAINER_LOCATION.TIMELINE}
              onRewind={onRewind}
              fps={fps}
              previewPlayerType={previewPlayerType}
              readonly
            />
          </If>
        </Box>
      </div>
    </div>
  )
}

PreviewToolbar.defaultProps = {
  fps: DEFAULT_FPS,
  progress: 0,
  duration: 0,
  onRewindTimeline: null,
  onPrevFrame: null,
  onNextFrame: null,
  isMedia: false,
  previewPlayerType: '',
  previewPlayerVideoElem: { current: null },
  asset: null,
  isLoading: false,
}

PreviewToolbar.propTypes = {
  playing: PropTypes.bool.isRequired,
  fps: PropTypes.number,
  progress: PropTypes.number,
  duration: PropTypes.number,

  onRewindTimeline: PropTypes.func,
  onPrevFrame: PropTypes.func,
  onPlayPause: PropTypes.func.isRequired,
  onNextFrame: PropTypes.func,
  isMedia: PropTypes.bool,
  previewPlayerType: PropTypes.string,
  previewPlayerVideoElem: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  asset: PropTypes.oneOfType([
    PT.VideoAsset, PT.ImageAsset, PT.AudioAsset, PT.TextAsset,
  ]),
  isLoading: PropTypes.bool,
}

export default React.memo(PreviewToolbar)
